<script setup lang="ts">
import {
    isNaturalProfileAttributeLocked,
    usePersonalAccountDataState,
} from '../../../../utilities/account/useAccountDataPersonal'
import BaseInputText from '../../../base/BaseInputText.vue'
import BaseInputCombobox from '../../../base/BaseInputCombobox.vue'
import BaseInputRadio from '../../../base/BaseInputRadio.vue'
import BaseInputDateSeparate from '../../../base/BaseInputDateSeparate.vue'
import { genderOptions, getAllCountries, pepOptions } from '../../../../data'
import { IdentificationIcon, UserCircleIcon, UserIcon } from '@heroicons/vue/solid'
import {
    isNaturalProfileAttributeHidden,
    isRequiredNaturalProfileAttributesForInvestment,
    isRequiredRequiredNaturalProfileAttributes,
} from '../../../../validation'
import { initPersonalAccountForm } from '../../../../utilities/account/useAccountFormPersonal'
import BaseInputSelect from '../../../base/BaseInputSelect.vue'
import { isPastPepOptionShown, isRequireAdditionalPepInformationEnabled } from '../../../../utilities/usePepSettings'
import { onMounted, ref } from 'vue'
import { getLanguages } from '../../../../utilities/api'

const { naturalProfile: account } = usePersonalAccountDataState()
const avaliableLanguages = ref([])

initPersonalAccountForm('Personal Data')

onMounted(async () => {
    avaliableLanguages.value = await getLanguages
    avaliableLanguages.value = avaliableLanguages.value.map((lang) => {
        return { label: lang.label, key: lang.value }
    })
})
</script>

<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-8 px-4 py-5 sm:grid-cols-2 sm:px-6">
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.first_name"
                :disabled="isNaturalProfileAttributeLocked('first_name')"
                :required="isRequiredRequiredNaturalProfileAttributes('first_name')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('first_name')"
                :icon="UserIcon"
                name="account.first_name"
                :label="$t('account.first_name')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.last_name"
                :disabled="isNaturalProfileAttributeLocked('last_name')"
                :required="isRequiredRequiredNaturalProfileAttributes('last_name')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('last_name')"
                :icon="UserIcon"
                name="account.last_name"
                :label="$t('account.last_name')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputDateSeparate
                v-model="account.date_of_birth"
                :disabled="isNaturalProfileAttributeLocked('date_of_birth')"
                :required="isRequiredRequiredNaturalProfileAttributes('date_of_birth')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('date_of_birth')"
                name="account.date_of_birth"
                :label="$t('account.date_of_birth')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCombobox
                v-model="account.nationality"
                :disabled="isNaturalProfileAttributeLocked('nationality')"
                :required="isRequiredRequiredNaturalProfileAttributes('nationality')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('nationality')"
                :icon="IdentificationIcon"
                name="account.nationality"
                :label="$t('account.nationality')"
                :options="getAllCountries()"
                nullable />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCombobox
                v-model="account.default_language"
                :disabled="isNaturalProfileAttributeLocked('default_language')"
                :icon="IdentificationIcon"
                name="account.default_language"
                :label="$t('account.default_language')"
                :options="avaliableLanguages"
                nullable />
        </div>
        <div v-if="!isNaturalProfileAttributeHidden('country_of_birth')" class="sm:col-span-1">
            <BaseInputCombobox
                v-model="account.country_of_birth"
                :icon="IdentificationIcon"
                :disabled="isNaturalProfileAttributeLocked('country_of_birth')"
                :required="isRequiredRequiredNaturalProfileAttributes('country_of_birth')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('country_of_birth')"
                name="account.country_of_birth"
                :label="$t('account.country_of_birth')"
                :options="getAllCountries()"
                nullable />
        </div>

        <div v-if="!isNaturalProfileAttributeHidden('city_of_birth')" class="sm:col-span-1">
            <BaseInputText
                v-model="account.city_of_birth"
                :disabled="isNaturalProfileAttributeLocked('city_of_birth')"
                :required="isRequiredRequiredNaturalProfileAttributes('city_of_birth')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('city_of_birth')"
                name="account.city_of_birth"
                :label="$t('account.city_of_birth')"></BaseInputText>
        </div>
        <div v-if="!isNaturalProfileAttributeHidden('gender')" class="sm:col-span-1">
            <BaseInputSelect
                v-model="account.gender"
                :disabled="isNaturalProfileAttributeLocked('gender')"
                :required="isRequiredRequiredNaturalProfileAttributes('gender')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('gender')"
                :icon="UserCircleIcon"
                name="account.gender"
                :label="$t('account.gender')"
                :options="genderOptions" />
        </div>
        <div
            :class="[
                'flex flex-wrap items-center sm:col-span-1',
                {
                    'sm:col-start-1': isRequireAdditionalPepInformationEnabled(),
                },
            ]">
            <BaseInputRadio
                v-model="account.pep"
                :required="isRequiredRequiredNaturalProfileAttributes('pep')"
                :disabled="isNaturalProfileAttributeLocked('pep')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('pep')"
                name="account.pep"
                :label="$t('account.pep')"
                :options="pepOptions" />
        </div>
        <div v-if="account.pep && isRequireAdditionalPepInformationEnabled()" class="sm:col-span-1">
            <BaseInputText
                v-model="account.pep_position"
                required
                :disabled="isNaturalProfileAttributeLocked('pep')"
                name="account.pep_position"
                :icon="IdentificationIcon"
                :label="$t('account.pep_position')" />
        </div>
        <div
            v-if="isPastPepOptionShown()"
            :class="[
                'flex flex-wrap items-center sm:col-span-1',
                {
                    'sm:col-start-1': isRequireAdditionalPepInformationEnabled(),
                },
            ]">
            <BaseInputRadio
                v-model="account.pep_past"
                :required="isRequiredRequiredNaturalProfileAttributes('pep')"
                :disabled="isNaturalProfileAttributeLocked('pep')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('pep')"
                name="account.pep_past"
                :label="$t('account.pep_past')"
                :options="pepOptions" />
        </div>
        <div v-if="account.pep_past && isPastPepOptionShown()" class="sm:col-span-1">
            <BaseInputText
                v-model="account.pep_past_position"
                required
                rules="required"
                :disabled="isNaturalProfileAttributeLocked('pep')"
                name="account.pep_past_position"
                :icon="IdentificationIcon"
                :label="$t('account.pep_position')" />
        </div>
    </div>
</template>
